import { Controller } from "react-hook-form";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  allowedFileTypes,
  ContentType,
  USER_ROLES_IDS,
  WAREHOUSE_IN_OUT_PERMIT_ATTACHMENTS_TYPE,
  WarehousePermitWorker,
} from "../../constants/common";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { useGetAllNationalities } from "../../hooks/common/query";
import { useTranslation } from "react-i18next";
import { formattedDate, mapToOptions } from "../../lib/functions";
import TextInput from "../inputs/TextInput";
import ComboboxInput from "../inputs/ComboboxInput";
import { Button } from "../button";
import DatePickerInput from "../inputs/DatePickerInput";
import toast from "react-hot-toast";
import useAuthStore from "../../store/Auth";
import { createAccompanyingPersonnelDetailsGroupCardSchema } from "../../schemas/accompanyingPersonnelDetailsGroupCardSchema";
import FileUploadInput from "../inputs/FileUploadInput";
import ViewAttachmentModal from "../modal/ViewAttachmentModal";
import { useGetWarehouseInOutPermitFileByType } from "../../hooks/permit/mutation";

interface AccompanyingPersonnelDetailsGroupCardProps {
  setIsAddVisitorFormVisible: React.Dispatch<React.SetStateAction<boolean>>;

  addedWorkers: WarehousePermitWorker[];
  setAddedWorkers: Dispatch<SetStateAction<WarehousePermitWorker[]>>;

  editingWorker: WarehousePermitWorker | null;
  setEditingWorker: React.Dispatch<
    React.SetStateAction<WarehousePermitWorker | null>
  >;

  isFieldsDisabled: boolean;
}

const AccompanyingPersonnelDetailsGroupCard = ({
  setAddedWorkers,
  setIsAddVisitorFormVisible,

  editingWorker,
  setEditingWorker,

  isFieldsDisabled,
}: AccompanyingPersonnelDetailsGroupCardProps) => {
  const [isAttachments, setIsAttachments] = useState({
    haveId: 0,
  });

  const uploadIdRef = useRef<HTMLInputElement | null>(null);
  const [idFileName, setIdFileName] = useState("");
  const [idFileType, setIdFileType] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useFormWithSchema(
    createAccompanyingPersonnelDetailsGroupCardSchema(isAttachments)
  );

  const [nationalities, setNationalities] = useState<
    { labelEn: string; labelAr: string; value: any }[]
  >([]);

  const { data: allNationalities } = useGetAllNationalities();

  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (editingWorker) {
      const userData = {
        id: editingWorker?.id,
        name: editingWorker?.name,
        nationality: editingWorker?.nationality,
        residencePermitNo: editingWorker?.residencePermitNo,
        issuingAuthority: editingWorker?.issuingAuthority,
        issueDate: editingWorker?.issueDate
          ? formattedDate(new Date(editingWorker?.issueDate))
          : "",
      };

      if (userData) {
        reset(userData);
      }

      if (editingWorker?.uploadId) {
        const file = editingWorker.uploadId as unknown as File;
        setIdFileName(file.name);
        setIdFileType(file.type);
      }

      setIsAttachments({
        haveId: editingWorker?.uploadId ? 1 : 0,
      });
    }
  }, [editingWorker, reset]);

  useEffect(() => {
    if (allNationalities) {
      const mappedNationalities = mapToOptions(
        "name",
        "name_Ar",
        "iso3",
        allNationalities
      );
      setNationalities(mappedNationalities);
    }
  }, [allNationalities, i18n.language]);

  const onSubmit = useCallback(
    async (data: any) => {
      const details = {
        name: data.name,
        nationality: data.nationality,
        residencePermitNo: data.residencePermitNo,
        issuingAuthority: data.issuingAuthority,
        issueDate: data.issueDate,

        uploadId: data.uploadId,
      };
      const id = uuidv4();

      if (editingWorker) {
        setAddedWorkers((prev) =>
          prev.map((worker) =>
            worker.id === editingWorker.id
              ? {
                  id: editingWorker.id,
                  ...details,
                  uploadId: details.uploadId || editingWorker.uploadId,
                }
              : worker
          )
        );
        setEditingWorker(null);
      } else {
        setAddedWorkers((prev) => [
          ...prev,
          {
            id,
            ...details,
          },
        ]);
      }

      reset();

      setIdFileName("");
      setIsAddVisitorFormVisible(false);
    },
    [
      editingWorker,
      reset,
      setIsAddVisitorFormVisible,
      setEditingWorker,
      setAddedWorkers,
    ]
  );

  const { user } = useAuthStore();

  //   const isApproval = useMemo(
  //     () =>
  //       location.pathname === APP_ROUTES.APPROVE_PERMIT ||
  //       location.pathname === APP_ROUTES.APPROVE_GROUP_REQUEST,
  //     [location.pathname]
  //   );

  const [previewFiles, setPreviewFiles] = useState<
    Record<number, { url: string; contentType: string }>
  >({});
  const [imageSrc, setImageSrc] = useState("");
  const [contentType, setContentType] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);
  const [selectedType, setSelectedType] = useState<number>(0);
  const [isGetFileByTypeLoading, setIsGetFileByTypeLoading] = useState(false);

  const {
    mutateAsync: GetWarehouseInOutPermitFileByType,
    isPending: isGetWarehouseInOutPermitFileByTypeLoading,
  } = useGetWarehouseInOutPermitFileByType();

  const handleAttachmentView = useCallback(
    async (type: number) => {
      try {
        // If we already have a local preview for this attachment type
        if (previewFiles && previewFiles[type]) {
          setImageSrc(previewFiles[type].url);
          setContentType(previewFiles[type].contentType);
          setIsViewAttachmentModalOpen(true);
          return;
        }

        if (editingWorker) {
          if (typeof editingWorker?.uploadId !== "boolean") {
            const file = editingWorker?.uploadId;

            if (typeof file === "object" && file instanceof File) {
              const url = URL.createObjectURL(file);
              setImageSrc(url);
              setContentType(file.type);
              setIsViewAttachmentModalOpen(true);
            }
            return;
          }
        }

        if (editingWorker?.id) {
          setSelectedType(type);

          const body = {
            Id: Number(editingWorker?.id),
            Type: type,
          };

          const res = await GetWarehouseInOutPermitFileByType(body);

          setIsGetFileByTypeLoading(isGetWarehouseInOutPermitFileByTypeLoading);

          if (res) {
            let url = "";
            if (res?.contentType !== ContentType.PDF) {
              url = `data:${res?.contentType};base64,${res?.photoFile}`;
            } else {
              const binaryString = atob(res?.photoFile);
              const byteArray = new Uint8Array(binaryString.length);

              for (let i = 0; i < binaryString.length; i++) {
                byteArray[i] = binaryString.charCodeAt(i);
              }

              const blob = new Blob([byteArray], { type: ContentType.PDF });
              url = URL.createObjectURL(blob);
            }
            setImageSrc(url);
            setContentType(res?.contentType);
            setIsViewAttachmentModalOpen(true);
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        setSelectedType(0);
      }
    },
    [
      GetWarehouseInOutPermitFileByType,
      editingWorker,
      isGetWarehouseInOutPermitFileByTypeLoading,
      previewFiles,
    ]
  );

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("name")}
                value={value}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />
          <Controller
            name="nationality"
            control={control}
            render={({ field: { name, onChange, value } }) => {
              return (
                <ComboboxInput
                  options={nationalities}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  label={t("nationality")}
                  required
                  value={
                    nationalities?.find((item) => item.value === value)
                      ?.labelEn || ""
                  }
                  readOnly={isFieldsDisabled}
                />
              );
            }}
          />

          <Controller
            name="residencePermitNo"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("residencePermitNo")}
                value={value}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="issuingAuthority"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("issuingAuthority")}
                value={value}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <Controller
            name="issueDate"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <DatePickerInput
                label={t("issueDate")}
                value={value || ""}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isFieldsDisabled}
              />
            )}
          />

          <FileUploadInput
            name="uploadId"
            label={t("uploadID")}
            accept=".png,.jpg,.jpeg,.pdf"
            allowedTypes={allowedFileTypes}
            isAttachments={isAttachments?.haveId}
            control={control}
            uploadRef={uploadIdRef}
            fileName={idFileName}
            setFileName={setIdFileName}
            setFileType={setIdFileType}
            handleAttachmentView={handleAttachmentView}
            selectedType={selectedType}
            attachmentType={
              WAREHOUSE_IN_OUT_PERMIT_ATTACHMENTS_TYPE.PERSONNEL_ID
            }
            isGetFileByTypeLoading={isGetFileByTypeLoading}
            error={errors.uploadId}
            previewFiles={previewFiles}
            setPreviewFiles={setPreviewFiles}
            disabled={isFieldsDisabled}
            isRemovable={false}
          />
        </div>

        <div className="flex items-center justify-between">
          <div />
          <div className="flex items-center gap-2">
            {!isFieldsDisabled && (
              <Button type="button" onClick={handleSubmit(onSubmit)}>
                {t("save")}
              </Button>
            )}
            <Button
              onClick={() => setIsAddVisitorFormVisible(false)}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-purple-primary/10"
            >
              {t("cancel")}
            </Button>
          </div>
        </div>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default memo(AccompanyingPersonnelDetailsGroupCard);
